'use client';
import TransitionLink from '@/components/TransitionLink';
import { useRef, useEffect, useState } from 'react';
import SplashButton from '@/components/SplashButton';
import { Content } from '@prismicio/client';
import Image from 'next/image';

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return isMobile;
}

export default function IndustryCard({
  industry,
  netSuiteForLabel,
  lang,
  learnMoreText,
}: {
  industry: Content.IndustryDocument;
  lang: string;
  netSuiteForLabel: string;
  learnMoreText: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile();

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Ignore AbortError or NotAllowedError
      });
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      if (!videoRef.current.paused) {
        videoRef.current.pause();
      }
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <TransitionLink
      key={industry.id}
      href={`/${lang}/industries/${industry.uid}`}
      className="group relative flex min-h-[320px] flex-col items-center justify-center overflow-hidden rounded-xl shadow-lg transition-transform before:absolute before:inset-0 before:z-0 before:bg-black hover:scale-[1.03]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isMobile ? (
        <Image
          src={`/video/${industry.uid}.jpg`}
          alt={industry.data.tiny_title || 'Industry'}
          fill
          className="absolute inset-0 z-[2] h-full w-full object-cover opacity-80 transition-opacity duration-300"
          sizes="100vw"
          priority={false}
        />
      ) : (
        <video
          ref={videoRef}
          className="absolute inset-0 z-[2] h-full w-full object-cover opacity-80 transition-opacity duration-300"
          src={`/video/${industry.uid}.mp4`}
          muted
          loop
          playsInline
          preload="auto"
        />
      )}
      <div className="absolute inset-0 z-[3] bg-black/50 transition-colors duration-300 group-hover:bg-black/20" />
      <h2 className="relative z-10 mb-4 px-6 text-center text-3xl font-normal leading-[1.15] text-white drop-shadow-lg transition-opacity duration-300 sm:mb-0 sm:group-hover:opacity-0">
        <span className="mb-1 block text-sm font-bold uppercase text-white/50 sm:mb-1">
          {netSuiteForLabel || 'NetSuite for'}
        </span>
        {industry.data.faq_title || 'Industry'}
      </h2>
      {/* Mobile: always show button below title */}
      <div className="relative z-20 mt-4 flex justify-center sm:hidden">
        <SplashButton as="button" text="Learn More" color="transparent" size="lg" />
      </div>
      {/* Desktop: fade-in overlay button on hover */}
      <div className="pointer-events-none absolute inset-0 z-10 hidden items-center justify-center sm:flex">
        <div className="pointer-events-auto opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <SplashButton
            as="button"
            text={learnMoreText || 'Learn More'}
            color="transparent"
            size="lg"
          />
        </div>
      </div>
    </TransitionLink>
  );
}
